// -----------------------------
// SocketService.ts
// -----------------------------
"use client";

import { useCustomer } from "@/providers/UserProvider";
import { useEffect, useState } from "react";
import Echo from "laravel-echo";
import Pusher from "pusher-js";

if (typeof window !== "undefined") {
    (window as any).Pusher = Pusher;
}

export enum SocketStatus {
    CONNECTING = "CONNECTING",
    CONNECTED = "CONNECTED",
    DISCONNECTED = "DISCONNECTED",
    ERROR = "ERROR",
}

export const SOCKET_URL = process.env.NEXT_PUBLIC_SOCKET_URL || "http://localhost:3000";

class SocketService {
    private static instance: SocketService;
    private echo: Echo<any> | null = null;
    private mockMode: boolean = false; // Enabled real Pusher Mode
    private mockListeners: Map<string, Function[]> = new Map();
    private mockStorage: Storage | null = typeof window !== "undefined" ? window.localStorage : null;
    private storageEventListener: ((event: StorageEvent) => void) | null = null;
    private userId: string | null = null;
    private trackingToken: string | null = null;
    private connectionStatus: SocketStatus = SocketStatus.DISCONNECTED;

    private constructor() { }

    public static getInstance(): SocketService {
        if (!SocketService.instance) SocketService.instance = new SocketService();
        return SocketService.instance;
    }

    public connect(userId: string | null, trackingToken: string | null = null): void {
        this.userId = userId; 
        this.trackingToken = trackingToken;

        if (this.mockMode) {
            console.log("[MockSocket] Connected with userId:", userId, "trackingToken:", trackingToken);
            this.connectionStatus = SocketStatus.CONNECTED;

            if (this.mockStorage && !this.storageEventListener) {
                this.storageEventListener = (event: StorageEvent) => {
                    if (event.key?.startsWith("socket_event_")) {
                        try {
                            const eventData = JSON.parse(event.newValue || "{}");
                            if (
                                eventData.recipients?.includes(userId) &&
                                this.mockListeners.has(eventData.event)
                            ) {
                                this.mockListeners.get(eventData.event)?.forEach(cb => cb(eventData.payload));
                            }
                        } catch (error) {
                            console.error("[MockSocket] Error parsing event data", error);
                        }
                    }
                };
                window.addEventListener("storage", this.storageEventListener);
            }
        } else {
            if (!this.echo) {
                this.connectionStatus = SocketStatus.CONNECTING;
                try {
                    this.echo = new Echo({
                        broadcaster: "pusher",
                        key: process.env.NEXT_PUSHER_APP_KEY || "cd290f4b0019278552cc",
                        cluster: process.env.NEXT_PUSHER_APP_CLUSTER || "ap2",
                        forceTLS: true,
                        wsHost: process.env.NEXT_PUSHER_HOST || "api-ap2.pusher.com",
                        wsPort: process.env.NEXT_PUSHER_PORT ? parseInt(process.env.NEXT_PUSHER_PORT) : 443,
                        wssPort: process.env.NEXT_PUSHER_PORT ? parseInt(process.env.NEXT_PUSHER_PORT) : 443,
                        enabledTransports: ["ws", "wss"],
                    });

                    this.echo.connector.pusher.connection.bind("connected", () => {
                        console.log("[Pusher] Connected to real Pusher websockets");
                        this.connectionStatus = SocketStatus.CONNECTED;
                    });

                    this.echo.connector.pusher.connection.bind("error", (error: any) => {
                        console.error("[Pusher] Connection error:", error);
                        this.connectionStatus = SocketStatus.ERROR;
                    });

                    this.echo.connector.pusher.connection.bind("disconnected", () => {
                        console.log("[Pusher] Real Pusher websockets disconnected");
                        this.connectionStatus = SocketStatus.DISCONNECTED;
                    });
                } catch (error) {
                    console.error("[Pusher] Failed to initialize Echo:", error);
                    this.connectionStatus = SocketStatus.ERROR;
                }
            }
        }
    }

    public disconnect(): void {
        if (this.mockMode) {
            if (this.storageEventListener) {
                window.removeEventListener("storage", this.storageEventListener);
                this.storageEventListener = null;
            }
            this.connectionStatus = SocketStatus.DISCONNECTED;
            console.log("[MockSocket] Disconnected");
        } else if (this.echo) {
            this.echo.disconnect();
            this.echo = null;
            this.connectionStatus = SocketStatus.DISCONNECTED;
        }
    }

    public on(event: string, callback: Function): void {
        if (this.mockMode) {
            if (!this.mockListeners.has(event)) this.mockListeners.set(event, []);
            this.mockListeners.get(event)?.push(callback);
        } else if (this.echo) {
            if (this.trackingToken) {
                console.log(`[Pusher] Subscribing to public channel order.${this.trackingToken} and listening to .${event}`);
                this.echo.channel(`order.${this.trackingToken}`)
                    .listen(`.${event}`, (payload: any) => {
                        console.log(`[Pusher] Received real-time event .${event} on guest channel:`, payload);
                        callback(payload);
                    });
            } else if (this.userId) {
                console.log(`[Pusher] Subscribing to users.${this.userId} and listening to .${event}`);
                this.echo.private(`users.${this.userId}`)
                    .listen(`.${event}`, (payload: any) => {
                        console.log(`[Pusher] Received real-time event .${event}:`, payload);
                        callback(payload);
                    });
            }
        }
    }

    public off(event: string, callback?: Function): void {
        if (this.mockMode) {
            if (callback) {
                const listeners = this.mockListeners.get(event) || [];
                this.mockListeners.set(event, listeners.filter(l => l !== callback));
            } else this.mockListeners.delete(event);
        } else if (this.echo) {
            if (this.trackingToken) {
                console.log(`[Pusher] Stopping listening to .${event} on channel order.${this.trackingToken}`);
                this.echo.channel(`order.${this.trackingToken}`).stopListening(`.${event}`);
            } else if (this.userId) {
                console.log(`[Pusher] Stopping listening to .${event} on channel users.${this.userId}`);
                this.echo.private(`users.${this.userId}`).stopListening(`.${event}`);
            }
        }
    }

    public emit(event: string, payload: any, recipients: string[] = []): void {
        if (this.mockMode && this.mockStorage) {
            recipients.forEach(recipientId => {
                const eventKey = `socket_event_${Date.now()}_${Math.random().toString(36).substring(2, 9)}_${recipientId}`;
                const eventData = { event, payload, recipients: [recipientId], sender: this.userId, timestamp: new Date().toISOString() };
                this.mockStorage?.setItem(eventKey, JSON.stringify(eventData));
                setTimeout(() => this.mockStorage?.removeItem(eventKey), 1000);
                if (recipientId === this.userId) this.mockListeners.get(event)?.forEach(cb => cb(payload));
            });
        } else {
            console.warn("[Pusher] Direct client emit ignored. Direct REST API calls must be used to trigger backend broadcasts.");
        }
    }

    public getStatus(): SocketStatus {
        return this.connectionStatus;
    }
}

export function useSocketInit(): SocketStatus {
    const [status, setStatus] = useState<SocketStatus>(SocketStatus.DISCONNECTED);
    const { user } = useCustomer();

    useEffect(() => {
        if (!user?.id) return;
        const socketService = SocketService.getInstance();
        socketService.connect(String(user.id));
        const interval = setInterval(() => setStatus(socketService.getStatus()), 500);
        return () => { socketService.disconnect(); clearInterval(interval); setStatus(SocketStatus.DISCONNECTED); };
    }, [user?.id]);

    return status;
}

export default SocketService;
